import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Typeahead, Highlighter } from "react-bootstrap-typeahead";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";

import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import Cross from "assets/images/close.svg";
import { useForm, Controller } from "react-hook-form";
import usePermissions from "hooks/usePermissions";
import { formatPrice } from "helpers/commonHelpers";
import errorToast from "helpers/errorToast";
import { addAdditionalApprovers, getApprovers, reapproveQuotation } from "api/services/quotation";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import Alert from "components/molecules/Alert/Alert";
import { useGetCostDetails } from "api/queries/quotation/useGetCostDetails";

const RequestApprovalDialog = function ({
  isOpen,
  close,
  quotationId,
  isBookAsDuplicate,
  isAdditionalApproval,
  isSuggestApproval,
  fetchQuoteRequestData,
  setShowBookQuoteDialog,
  setBookingPayload,
  setBookingType,
  status,
}) {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const typeaheadRef = useRef(null);
  const { permissions } = usePermissions();
  const { email: userEmail } = JSON.parse(permissions);
  const { setError, clearErrors, control, handleSubmit } = useForm({
    shouldFocusError: true,
  });

  const [approverOptions, setApproverOptions] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalTitle = isAdditionalApproval
    ? intl.formatMessage({ id: "shipment-quotes.components.requestApprovalDialog.addAdditionalApprovers" })
    : intl.formatMessage({ id: "shipment-quotes.components.requestApprovalDialog.requestApproval" });

  const { data: costDetailsData } = useGetCostDetails(quotationId, {
    enabled: !!quotationId,
    refetchOnMount: false,
  });

  /** Get list of approvers if dialog is opened, reset state if closed */
  useEffect(() => {
    if (isOpen) getListOfApprovers();
    else {
      clearErrors();
      setApproverOptions([]);
      setSelectedApprovers([]);
      setComment("");
    }
  }, [isOpen]);

  /** [GET] Gets a list of users that can be requested approval from */
  const getListOfApprovers = () => {
    setIsLoading(true);
    const query = `/list-approvers?include_preapprover=true&allow_below_level=true`;
    getApprovers(quotationId, query)
      .then((res) => {
        const allApprovers = res.data.users.map(({ id, name, email, is_preapprover, is_staged, is_invited }) => ({
          id,
          label: name,
          email,
          is_preapprover,
          is_staged,
          is_invited,
        }));
        const availableApprovers = allApprovers.filter(({ email, is_invited }) => email !== userEmail && !is_invited);
        const invitedApprovers = allApprovers.filter(({ is_invited }) => is_invited);
        setApproverOptions(availableApprovers);
        setSelectedApprovers(invitedApprovers);
      })
      .catch((err) =>
        errorToast(
          err,
          <FormattedMessage
            id="shipment-quotes.components.requestApprovalDialog.couldNotRetrieveApprovers"
            defaultMessage="Could not retrieve list of approvers. Please try again later."
          />,
        ),
      )
      .finally(() => setIsLoading(false));
  };

  /** [POST] Submit the approval request */
  const requestApproval = () => {
    if (selectedApprovers?.length === 0) {
      setError("selected_approvers", { type: "invalid", message: "Please select a user" });
      return;
    }


    const payload = {
      approval_request_to: selectedApprovers.map(({ id }) => id),
      approval_comment: comment,
      ...(isSuggestApproval && { suggest_approval: true }),
    };
    if (status === "reapproval_needed_after_booking") {
      confirmRequestReapproval(payload);
    } else {
      setBookingPayload(payload);
      setBookingType(isSuggestApproval ? "suggest_approval" : "request_approval");
      close();
      setShowBookQuoteDialog(true);
    }
  };

  /** [PUT] Submit the additional approvers request */
  const onAddAdditionalApprovers = () => {
    setIsSubmitting(true);
    if (selectedApprovers?.length === 0) {
      setError("selected_approvers", { type: "invalid", message: "Please select a user" });
      return;
    }

    const payload = {
      approvers: selectedApprovers.filter(({ is_invited }) => !is_invited).map(({ id }) => id),
      approval_comment: comment,
    };
    addAdditionalApprovers(quotationId, payload)
      .then((res) => {
        toast(res.data.message, { type: "success" });
        fetchQuoteRequestData();
        close();
      })
      .catch((err) =>
        errorToast(
          err,
          <FormattedMessage
            id="shipment-quotes.components.requestApprovalDialog.couldNotSubmitApprovalRequest"
            defaultMessage="Could not submit approval request. Please try again later."
          />,
        ),
      )
      .finally(() => setIsSubmitting(false));
  };

  const confirmRequestReapproval = (payload) => {
    reapproveQuotation(quotationId, payload)
      .then((res) => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userPendingTasks });
        const toastMessage = (
          <FormattedMessage
            id="shipment-quotes.components.bookQuoteDialog.approvalRequested"
            defaultMessage="Approval requested"
          />
        );
        toast(toastMessage, {
          type: "success",
        });
        queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(res.data.shipment_id) });
        fetchQuoteRequestData();
        close();
      })
      .catch((err) =>
        errorToast(
          err,
          <FormattedMessage
            id="shipment-quotes.components.requestApprovalDialog.couldNotSubmitApprovalRequest"
            defaultMessage="Could not submit approval request. Please try again later."
          />,
        ),
      )
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog size="md" title={modalTitle} show={isOpen} onHide={close} isLoading={isLoading}>
      {isBookAsDuplicate && !isAdditionalApproval && (
        <Alert variant="warning" className="mb-4">
          <FormattedMessage
            id="shipment-quotes.components.bookQuoteDialog.warningMessage"
            defaultMessage="You will create a shipment with a new shipment ID, please make sure your logistics provider is aware and able to provide the selected service."
          />
        </Alert>
      )}
      {status === "reapproval_needed_after_booking" && (
        <Row className="mb-2">
          <Col className="w-192">
            <FormattedMessage
              id="shipment-quotes.components.manageApprovalDialog.re-approval"
              defaultMessage="This is re-approval request for"
            />{" "}
            <span className="font-weight-bold">
              {(
                [
                  ...(costDetailsData?.costs?.post_booking_in_transit_costs_data?.["Origin Charges"] ?? []),
                  ...(costDetailsData?.costs?.post_booking_in_transit_costs_data?.Freight ?? []),
                  ...(costDetailsData?.costs?.post_booking_in_transit_costs_data?.["Destination Charges"] ?? []),
                ]
                  ?.filter((cost) => !cost.approved_by_shipper)
                  .map(
                    (cost) =>
                      `${cost.additional_cost_description || cost.description} (${formatPrice(
                        cost.amount_target,
                        cost.target_currency_code,
                      )})`,
                  ) ?? []
              ).join(", ")}
            </span>
          </Col>
        </Row>
      )}
      <p className="mb-1 text-secondary">
        <FormattedMessage
          id="shipment-quotes.components.requestApprovalDialog.identifyPossibleApprovers"
          defaultMessage="Please identify one or multiple possible approvers from the list below."
        />
      </p>
      <p className="text-secondary">
        <FormattedMessage
          id="shipment-quotes.components.requestApprovalDialog.note"
          defaultMessage="Please note, each identified approver receives a notification."
        />
      </p>
      <form onSubmit={handleSubmit(isAdditionalApproval ? onAddAdditionalApprovers : requestApproval)}>
        {selectedApprovers.map((selectedApprover) => (
          <div className="d-flex justify-content-between py-3 px-4 mb-2 br-2  align-items-center" key={selectedApprover.id}>
            <p className="mb-0">{selectedApprover.label}</p>
            {!selectedApprover.is_invited && (
              <img
                src={Cross}
                alt="unselect approver"
                style={{ width: 12, height: 12, cursor: "pointer", opacity: 0.2 }}
                onClick={() => {
                  setApproverOptions([...approverOptions, selectedApprover]);
                  setSelectedApprovers(selectedApprovers.filter(({ id }) => id !== selectedApprover.id));
                }}
              />
            )}
          </div>
        ))}
        <div className="mt-4">
          <p className="font-weight-bold mb-2 required">
            <FormattedMessage
              id="shipment-quotes.components.requestApprovalDialog.requestApprovalFrom"
              defaultMessage="Request approval from"
            />
          </p>
          <Controller
            control={control}
            name="selected_approvers"
            render={({ fieldState }) => (
              <>
                <Typeahead
                  ref={typeaheadRef}
                  onChange={(event) => {
                    if (event[0]) {
                      clearErrors("selected_approvers");
                      setSelectedApprovers([...selectedApprovers, event[0]]);
                      setApproverOptions(approverOptions.filter((approver) => approver.id !== event[0].id));
                      typeaheadRef.current.clear();
                    }
                  }}
                  inputProps={{
                    name: "selected_approvers",
                    style: { height: 26, borderRadius: 10, padding: 11, fontSize: 11, zIndex: isOpen ? 1115 : 0 },
                    className: fieldState.error ? "invalid-input bg-white" : `${isOpen ? "bg-white search-box" : ""}`,
                  }}
                  options={approverOptions}
                  renderMenuItemChildren={({ label, email }, { text }) => (
                    <div className="p-2">
                      <div className="font-weight-bold font-12">
                        <Highlighter search={text}>{label}</Highlighter>
                      </div>{" "}
                      <div className="font-b3 mt-1 text-secondary">
                        <Highlighter search={text}>{email}</Highlighter>
                      </div>
                    </div>
                  )}
                  filterBy={["label", "email"]}
                  placeholder="Search users..."
                  id="selected_approvers"
                />
                {fieldState.error && <span className="text-danger">{fieldState.error.message}</span>}
              </>
            )}
          />
        </div>

        <div className="mt-4">
          <p className="font-weight-bold mb-2">Comments</p>
          <Form.Control as="textarea" rows={2} onChange={(e) => setComment(e.target.value)} />
        </div>

        <Row className="mb-3 mt-4">
          <Col lg={12}>
            <div className="d-block border-grey border-top w-100" />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col lg={12} className="d-flex flex-row">
            <Button
              className="btn-grey"
              size="lg"
              label={intl.formatMessage({
                id: "general.cancel",
                defaultMessage: "Cancel",
              })}
              onClick={close}
            />
            <Button
              className="ml-auto"
              size="lg"
              variant="success"
              label={intl.formatMessage({
                id: "shipment-quotes.components.requestApprovalDialog.sendApprovalRequest",
                defaultMessage: "Send approval request",
              })}
              isLoading={isSubmitting}
              type="submit"
            />
          </Col>
        </Row>
      </form>
    </Dialog>
  );
};

RequestApprovalDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  quotationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isBookAsDuplicate: PropTypes.bool.isRequired,
  fetchQuoteRequestData: PropTypes.func.isRequired,
  isAdditionalApproval: PropTypes.bool.isRequired,
  isSuggestApproval: PropTypes.bool.isRequired,
  status: PropTypes.string,
  setShowBookQuoteDialog: PropTypes.func.isRequired,
  setBookingPayload: PropTypes.func.isRequired,
  setBookingType: PropTypes.func.isRequired,
};

RequestApprovalDialog.defaultProps = {
  quotationId: null,
  status: null,
};

export default RequestApprovalDialog;
